import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtQty, fmtCHF } from "@/lib/format";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, Sel, opts } from "@/components/Field";
import { FormDialog, useSubmit, num, clean } from "./FormDialog";

const TITLES = { in: "Registra entrata", out: "Registra uscita", return: "Registra restituzione" };

export const MovementDialog = ({ open, onOpenChange, mode = "in", productId, eventId, shoppingItem, onSaved }) => {
  const { assoc, user, can } = useAuth();
  const [inv, setInv] = useState(null);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [lots, setLots] = useState([]);
  const [f, setF] = useState({});
  const { saving, run } = useSubmit();
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!open) return;
    setF({ product_id: productId || shoppingItem?.product_id || "", lot_id: "", quantity: shoppingItem?.quantity ?? "", event_id: eventId || shoppingItem?.event_id || "",
      reason: eventId ? "Evento" : "", picked_up_by: user?.name || "", recipient: "", supplier: shoppingItem?.store || "", document: shoppingItem?.receipt || "",
      lot_number: "", expiry_date: "", location: "", notes: "", unit_cost: "" });
    setInv(null);
    api.get("/products?page_size=200&status=active").then((r) => setProducts(r.data.items));
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [open, productId, eventId, shoppingItem, user]);

  useEffect(() => {
    if (!open || !f.product_id) return setLots([]);
    api.get(`/products/${f.product_id}`).then((r) => {
      const ls = r.data.lots.filter((l) => l.qty_remaining > 0);
      setLots(ls);
      if (mode !== "in") setF((s) => ({ ...s, lot_id: (ls.find((l) => l.suggested) || ls[0])?.id || "" }));
      else setF((s) => ({ ...s, location: s.location || r.data.location || "", unit_cost: r.data.purchase_price ?? "" }));
      setInv((v) => (v ? { ...v, unit_price: r.data.sale_price ?? "" } : v));
    });
  }, [open, f.product_id, mode]);

  const product = products.find((p) => p.id === f.product_id);
  const lotOpts = useMemo(() => lots.map((l) => ({ value: l.id, label: `${l.lot_number || "Lotto senza numero"} · ${fmtQty(l.qty_remaining)} disp.${l.expiry_date ? ` · scad. ${fmtDate(l.expiry_date)}` : ""}${l.suggested ? " · usa per primo" : ""}` })), [lots]);

  const submit = () =>
    run(async () => {
      const q = num(f.quantity);
      if (mode === "out") {
        const invoice = inv ? clean({ ...inv, unit_price: num(inv.unit_price) }) : null;
        const { data } = await api.post("/movements/out", clean({ product_id: f.product_id, lot_id: f.lot_id, quantity: q, picked_up_by: f.picked_up_by, recipient: f.recipient || inv?.recipient_name, event_id: f.event_id, reason: f.reason, document: f.document, notes: f.notes, invoice }));
        if (data.invoice_number) {
          const msg = { sent: "inviata per e-mail", not_configured: "creata ma NON inviata: servizio e-mail non configurato", failed: "creata ma invio e-mail non riuscito" }[data.invoice_email_status];
          toast[data.invoice_email_status === "sent" ? "success" : "warning"](`Fattura ${data.invoice_number} ${msg}`, { duration: 8000 });
        }
      } else {
        await api.post("/movements/in", clean({ product_id: f.product_id, kind: mode, lot_id: f.lot_id, quantity: q, unit_cost: mode === "in" ? num(f.unit_cost) : null, lot_number: f.lot_number, expiry_date: f.expiry_date, supplier: f.supplier, location: f.location, document: f.document, notes: f.notes, event_id: f.event_id, shopping_item_id: shoppingItem?.id }));
      }
      onOpenChange(false);
      onSaved?.();
    }, "Movimento registrato");

  const eventOpts = events.map((e) => ({ value: e.id, label: `${e.name} (${fmtDate(e.start)})` }));
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={TITLES[mode]} onSubmit={submit} saving={saving} testId={`movement-${mode}-dialog`} submitLabel="Registra">
      <Field label="Prodotto *"><Sel testId="movement-product-select" value={f.product_id} onChange={set("product_id")} placeholder="Scegli un prodotto" options={products.map((p) => ({ value: p.id, label: `${p.name} (${fmtQty(p.quantity)} ${p.unit})` }))} /></Field>
      {mode === "in" ? (
        <Field label="Lotto"><Sel testId="movement-lot-select" value={f.lot_id} onChange={set("lot_id")} placeholder="Nuovo lotto" options={lotOpts} /></Field>
      ) : (
        <Field label="Lotto *" hint={lots.length ? "Suggerito il lotto con scadenza più vicina" : f.product_id ? "Nessun lotto disponibile" : ""}>
          <Sel testId="movement-lot-select" value={f.lot_id} onChange={set("lot_id")} placeholder="Scegli il lotto" options={lotOpts} />
        </Field>
      )}
      <Field label={`Quantità *${product ? ` (${product.unit})` : ""}`}><Input data-testid="movement-quantity-input" type="number" min="0" step="any" required value={f.quantity} onChange={set("quantity")} className="h-11" /></Field>
      {mode === "in" && (
        <Field label="Prezzo d'acquisto per unità (CHF)" hint={f.unit_cost && f.quantity ? `Totale ${fmtCHF(Number(f.unit_cost) * Number(f.quantity))}` : "Facoltativo"}>
          <Input data-testid="movement-unit-cost-input" type="number" min="0" step="0.05" value={f.unit_cost} onChange={set("unit_cost")} className="h-11" />
        </Field>
      )}
      {mode === "in" && !f.lot_id && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Numero di lotto"><Input data-testid="movement-lotnumber-input" value={f.lot_number} onChange={set("lot_number")} className="h-11" /></Field>
          <Field label="Data di scadenza"><Input data-testid="movement-expiry-input" type="date" value={f.expiry_date} onChange={set("expiry_date")} className="h-11" /></Field>
          <Field label="Ubicazione"><Sel testId="movement-location-select" value={f.location} onChange={set("location")} options={opts(assoc?.locations)} placeholder="—" /></Field>
          <Field label="Fornitore"><Input data-testid="movement-supplier-input" value={f.supplier} onChange={set("supplier")} className="h-11" /></Field>
        </div>
      )}
      {mode === "out" && (
        <>
          <Field label="Motivo *"><Sel testId="movement-reason-select" value={f.reason} onChange={set("reason")} placeholder="Scegli il motivo" options={opts(assoc?.exit_reasons)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Chi ritira" hint="Se diverso da chi registra"><Input data-testid="movement-pickedup-input" value={f.picked_up_by} onChange={set("picked_up_by")} className="h-11" /></Field>
            <Field label="Destinatario"><Input data-testid="movement-recipient-input" value={f.recipient} onChange={set("recipient")} className="h-11" /></Field>
          </div>
        </>
      )}
      {eventOpts.length > 0 && <Field label="Evento collegato"><Sel testId="movement-event-select" value={f.event_id} onChange={set("event_id")} placeholder="Nessuno" options={eventOpts} /></Field>}
      <Field label={mode === "out" ? "Giustificativo" : "Documento di acquisto"} hint="Numero o riferimento, facoltativo"><Input data-testid="movement-document-input" value={f.document} onChange={set("document")} className="h-11" /></Field>
      {mode === "out" && can("invoices", "create") && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3">
          <label className="flex items-center gap-3 font-semibold text-slate-900 cursor-pointer">
            <Checkbox data-testid="movement-invoice-checkbox" checked={!!inv} onCheckedChange={(c) => setInv(c ? { recipient_name: f.recipient || "", email: "", street: "", pcode: "", city: "", unit_price: product?.sale_price ?? "" } : null)} />
            Crea e invia fattura
          </label>
          {inv && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Intestatario *" className="col-span-2"><Input data-testid="invoice-name-input" required value={inv.recipient_name} onChange={(e) => setInv({ ...inv, recipient_name: e.target.value })} className="h-11" /></Field>
              <Field label="E-mail *" className="col-span-2"><Input data-testid="invoice-email-input" type="email" required value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} className="h-11" /></Field>
              <Field label="Via e numero" className="col-span-2"><Input data-testid="invoice-street-input" value={inv.street} onChange={(e) => setInv({ ...inv, street: e.target.value })} className="h-11" /></Field>
              <Field label="NPA"><Input data-testid="invoice-pcode-input" value={inv.pcode} onChange={(e) => setInv({ ...inv, pcode: e.target.value })} className="h-11" /></Field>
              <Field label="Località"><Input data-testid="invoice-city-input" value={inv.city} onChange={(e) => setInv({ ...inv, city: e.target.value })} className="h-11" /></Field>
              <Field label="Prezzo per unità (CHF) *" hint={inv.unit_price !== "" && f.quantity ? `Totale fattura ${fmtCHF(Number(inv.unit_price) * Number(f.quantity))}` : ""} className="col-span-2">
                <Input data-testid="invoice-unit-price-input" type="number" min="0" step="0.05" required value={inv.unit_price} onChange={(e) => setInv({ ...inv, unit_price: e.target.value })} className="h-11" />
              </Field>
            </div>
          )}
        </div>
      )}
      <Field label="Note"><Textarea data-testid="movement-notes-input" value={f.notes} onChange={set("notes")} rows={2} /></Field>
    </FormDialog>
  );
};
